import {
  ActivityStage,
  ActivityStatus,
  Correctness,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';

import { LaunchSources } from '~analytics/types';

import {
  LaunchMultipleChoiceEventAdapter,
  LaunchShortAnswerEventAdapter,
  LaunchTrueFalseEventAdapter,
  LaunchVoteEventAdapter,
} from '../../analytics';
import axios from '../localAxios';

export type QuestionScore = {
  id: number;
  activityInstanceId: number;
  questionId: number;
  gradingWeight: number;
  ungradedScore: number;
  updatedDate: string;
  studentQuestionScores: Array<StudentQuestionScore>;
};

export type StudentQuestionScore = {
  id: number;
  studentId: number;
  score: number;
  correctness: Correctness;
  updatedDate: string;
  note: string;
};

export type Room = {
  id: number;
  nameLower: string;
  isRostered: boolean;
};

type Setting = {
  showResults: boolean;
  showFeedback: boolean;
  requireNames: boolean;
  randomQuestions: boolean;
  randomAnswers: boolean;
  pacing: string;
  oneAttempt: boolean;
  allowStudentNav: boolean;
};

export type Student = {
  id: number;
  name: string;
  userUuid: string;
  isFinished: boolean;
};

type Response = {
  questionId: number;
  textResponse: string;
  isCorrect: boolean;
  team: -1;
  hidden: boolean;
  userUuid: string;
  id: number;
};

export type Activity = {
  id: number;
  quizId: number;
  // Source shared library the quiz was launched from; null for personal quizzes.
  workspaceId?: string | null;
  workspaceName?: string | null;
  room: Room;
  startTime: string;
  endTime: string;
  activityState: number;
  activityStage: ActivityStage;
  state: ActivityStatus;
  hideReport: boolean;
  activityType: string;
  settings: Setting;
  students: Array<Student>;
  responses: Array<Response>;
  questionScores: Array<QuestionScore>;
  passRate: number | null;
};

const baseURL = `${window.APP_CONFIG.TEACHER_SERVICE}`;
const legacyBaseURL = `${window.APP_CONFIG.BACKEND_HOST}`;

const getCurrentActivitiesByRoom = async (roomId: number) => {
  return axios.get<Activity[]>(`${baseURL}/rooms/${roomId}/activities`);
};

const getActivityById = async (activityInstanceId: number) => {
  return axios.get<Activity>(`${baseURL}/activities/${activityInstanceId}`);
};

const updateActivityState = async (
  activityInstanceId: number,
  questionPosition: number
) => {
  return axios.put<Activity>(
    `${baseURL}/activities/${activityInstanceId}/update-activity-state`,
    {
      questionPosition: questionPosition,
    }
  );
};

const toggleActivityStatus = async (activityInstanceId: number) => {
  return axios.put<Activity>(
    `${baseURL}/activities/${activityInstanceId}/toggle-activity-status`
  );
};

const finishActivity = async (activityInstanceId: number) => {
  return axios.put<Activity>(
    `${baseURL}/activities/${activityInstanceId}/finish-activity`
  );
};

const updatePassRate = async (activityInstanceId: number, passRate: number) => {
  return axios.put<Activity>(
    `${baseURL}/activities/${activityInstanceId}/pass-rate`,
    {
      pass_rate: passRate,
    }
  );
};

const legacyStartQuickQuestion = async ({
  room,
  questionType,
  questionText,
  allowRepeatResponses,
  requireNames,
  oneAttempt,
}) => {
  return axios
    .post(`${legacyBaseURL}/lecturers/api/start-quick-question`, {
      room_name: room.nameLower.toLowerCase(),
      question_type: questionType,
      question_text: questionText,
      allow_repeat_responses: allowRepeatResponses,
      require_names: requireNames,
      one_attempt: oneAttempt,
    })
    .then((result) => {
      const isRosteredRoom = room.isRostered;
      switch (questionType) {
        case QuestionTypes.TrueFalse:
          new LaunchTrueFalseEventAdapter(
            isRosteredRoom,
            LaunchSources.Question
          ).sendAnalytics();
          break;
        case QuestionTypes.MultipleChoice:
          new LaunchMultipleChoiceEventAdapter(
            isRosteredRoom,
            LaunchSources.Question
          ).sendAnalytics();
          break;
        case QuestionTypes.FreeResponse:
          new LaunchShortAnswerEventAdapter(
            {},
            isRosteredRoom,
            LaunchSources.Question
          ).sendAnalytics();
          break;
      }

      return result.data.id;
    });
};

const legacyStartVote = async (room: Room) => {
  return axios
    .post(`${legacyBaseURL}/lecturers/api/convert-quick-question-to-vote`, {
      room_name: room.nameLower.toLowerCase(),
      require_names: false,
    })
    .then((results) => {
      const isRosteredRoom = room.isRostered;
      new LaunchVoteEventAdapter(isRosteredRoom).sendAnalytics();
      return results.data;
    });
};

const legacyHideResponse = async (responseId: number, roomName: string) => {
  const formData = new FormData();
  formData.append('room_name', roomName);
  formData.append('response_id', responseId as unknown as string);

  return axios.post(`${legacyBaseURL}/students/api/hide-response`, formData);
};

export {
  getActivityById,
  getCurrentActivitiesByRoom,
  updateActivityState,
  toggleActivityStatus,
  finishActivity,
  updatePassRate,
  legacyStartQuickQuestion,
  legacyStartVote,
  legacyHideResponse,
};
